'use client'

import { Modal } from 'antd'
import { useCallback, useState, type ComponentType } from 'react'
import useI18n from 'Hooks/useI18n'
import TipsyButton from 'Comps/ui-kit/TipsyButton'

type GuideModalShellProps = {
  open: boolean
  onClose: () => void
  pages: ComponentType[]
}

const MODAL_STYLES = {
  content: {
    padding: 0,
    borderRadius: '20px',
    background: 'rgba(42, 33, 36, 0.95)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)',
  },
  body: {
    padding: 0,
  },
} as const

export default function GuideModalShell({
  open,
  onClose,
  pages,
}: GuideModalShellProps) {
  const { t } = useI18n()
  const [page, setPage] = useState(0)
  const totalPages = pages.length

  const handleClose = useCallback(() => {
    onClose()
    setTimeout(() => setPage(0), 300)
  }, [onClose])

  const isLastPage = page === totalPages - 1
  const PageComponent = pages[page]

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      centered
      destroyOnClose={false}
      closable={false}
      styles={MODAL_STYLES}
    >
      <div className="relative px-8 pb-6 pt-8">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-5 top-5 flex h-6 w-6 items-center justify-center rounded-full text-white/50 transition hover:text-white"
          aria-label={t('Close')}
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L13 13M1 13L13 1"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          </svg>
        </button>

        <PageComponent />

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {Array.from({ length: totalPages }).map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full transition ${
                  i === page ? 'bg-white' : 'bg-white/25'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2.5">
            {page > 0 && (
              <TipsyButton
                className="relative h-10 w-[153px] text-white"
                onClick={() => setPage((p) => p - 1)}
              >
                {t('Previous')}
              </TipsyButton>
            )}
            <TipsyButton
              className="relative h-10 w-[153px] text-white"
              variant="filled"
              onClick={isLastPage ? handleClose : () => setPage((p) => p + 1)}
            >
              {isLastPage ? t('Got it') : t('Next')}
            </TipsyButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}
